import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { 
  Loader2, CheckCircle2, Phone, User as UserIcon, MessageSquare, 
  Copy, AlertCircle, Search, Clock, CheckCircle, XCircle, Tag, Ban
} from 'lucide-react';
import { toast } from 'sonner';

const PublicComplaint = () => {
  const [cfg, setCfg] = useState(null);
  const [cfgLoading, setCfgLoading] = useState(true);

  const [tab, setTab] = useState('submit');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);
  const [submitError, setSubmitError] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [problemType, setProblemType] = useState('');
  const [remarks, setRemarks] = useState('');

  const [ticketInput, setTicketInput] = useState('');
  const [checkLoading, setCheckLoading] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [statusList, setStatusList] = useState(null);
  const [statusError, setStatusError] = useState('');
  const [searchMode, setSearchMode] = useState('ticket'); // 'ticket' | 'phone'

  useEffect(() => {
    api.getPublicComplaintConfig()
      .then((res) => setCfg(res.data))
      .catch(() => setCfg({ is_enabled: true, title: 'Need Help?', subtitle: '', primary_color: '#0891b2', problem_types: [], cta_text: 'Submit Complaint' }))
      .finally(() => setCfgLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (name.trim().length < 2) { setSubmitError('Please enter your full name'); return; }
    const cleanPhone = phone.replace(/[^0-9+]/g, '');
    if (cleanPhone.length < 10) { setSubmitError('Please enter a valid phone number'); return; }
    if (remarks.trim().length < 5) { setSubmitError('Please describe your complaint'); return; }

    setSubmitting(true);
    try {
      // Start live chat — student is taken to a waiting room where an agent joins in ≤30s.
      // If no agent is available within 30s, backend auto-creates a legacy ticket as fallback.
      const res = await api.startComplaintChat({
        student_name: name.trim(),
        phone: cleanPhone,
        problem_type: problemType || 'General',
        message: remarks.trim(),
      });
      const { chat_id, token } = res.data;
      // Persist so refresh keeps them in the chat
      try { localStorage.setItem(`complaint_chat_${chat_id}`, token); } catch (e) { /* noop */ }
      window.location.href = `/complaint/chat/${chat_id}?t=${encodeURIComponent(token)}`;
    } catch (err) {
      setSubmitError(err.response?.data?.detail || 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCheck = async (e) => {
    e.preventDefault();
    setStatusError('');
    setStatusData(null);
    setStatusList(null);
    const input = ticketInput.trim();
    if (!input) {
      setStatusError(searchMode === 'ticket' ? 'Please enter your ticket number' : 'Please enter your phone number');
      return;
    }
    setCheckLoading(true);
    try {
      if (searchMode === 'phone') {
        const cleanPhone = input.replace(/[^0-9+]/g, '');
        if (cleanPhone.length < 10) {
          setStatusError('Please enter a valid phone number');
          setCheckLoading(false);
          return;
        }
        const res = await api.getPublicComplaintStatusByPhone(cleanPhone);
        setStatusList(res.data);
      } else {
        const res = await api.getPublicComplaintStatus(input.toUpperCase());
        setStatusData(res.data);
      }
    } catch (err) {
      setStatusError(err.response?.data?.detail || 'Not found. Please check and try again.');
    } finally {
      setCheckLoading(false);
    }
  };

  const copyTicket = () => {
    navigator.clipboard.writeText(submitted.ticket_number);
    toast.success('Ticket number copied');
  };

  const resetForm = () => {
    setSubmitted(null);
    setName(''); setPhone(''); setProblemType(''); setRemarks('');
  };

  const statusBadge = (status) => {
    const map = {
      open: { bg: 'bg-amber-100', text: 'text-amber-800', icon: <Clock size={14} />, label: 'Open — In Queue' },
      'in-progress': { bg: 'bg-blue-100', text: 'text-blue-800', icon: <Loader2 size={14} className="animate-spin" />, label: 'In Progress' },
      in_progress: { bg: 'bg-blue-100', text: 'text-blue-800', icon: <Loader2 size={14} className="animate-spin" />, label: 'In Progress' },
      solved: { bg: 'bg-green-100', text: 'text-green-800', icon: <CheckCircle size={14} />, label: 'Solved' },
      escalated: { bg: 'bg-purple-100', text: 'text-purple-800', icon: <AlertCircle size={14} />, label: 'Escalated to Admin' },
      closed: { bg: 'bg-slate-100', text: 'text-slate-700', icon: <XCircle size={14} />, label: 'Closed' },
    };
    const s = map[status] || map.open;
    return (
      <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${s.bg} ${s.text}`}>
        {s.icon}{s.label}
      </span>
    );
  };

  if (cfgLoading || !cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <Loader2 className="w-8 h-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  const color = cfg.primary_color || '#0891b2';
  const problemTypes = (cfg.problem_types && cfg.problem_types.length) ? cfg.problem_types : ['Other'];

  // Disabled state
  if (cfg.is_enabled === false) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-full mx-auto flex items-center justify-center mb-4">
            <Ban size={32} className="text-slate-400" />
          </div>
          <h1 className="text-xl font-bold text-slate-900 mb-2">Form Temporarily Unavailable</h1>
          <p className="text-sm text-slate-600">
            Our complaint form is currently disabled. Please contact us directly via phone or email.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4 py-8"
      style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}dd 50%, #0f172a 100%)` }}
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="public-complaint-page">
        {/* Header */}
        {cfg.logo_url ? (
          <div
            className="relative h-44"
            style={{ background: `url(${cfg.logo_url}) center/cover` }}
          >
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/60" />
            <div className="absolute inset-x-0 bottom-0 p-5 text-white text-center">
              <h1 className="text-2xl font-bold drop-shadow">{cfg.title || 'Need Help?'}</h1>
              {cfg.subtitle && <p className="text-sm mt-1 drop-shadow opacity-95">{cfg.subtitle}</p>}
            </div>
          </div>
        ) : (
          <div
            className="text-white py-8 px-6 text-center"
            style={{ background: `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)` }}
          >
            <div className="inline-flex items-center justify-center w-14 h-14 bg-white/15 rounded-full mb-3 backdrop-blur">
              <MessageSquare size={28} />
            </div>
            <h1 className="text-2xl font-bold mb-1">{cfg.title || 'Need Help?'}</h1>
            {cfg.subtitle && <p className="text-sm opacity-95">{cfg.subtitle}</p>}
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          <button
            onClick={() => setTab('submit')}
            data-testid="tab-submit"
            className={`flex-1 py-3 text-sm font-semibold transition ${
              tab === 'submit' ? 'bg-white text-slate-900 -mb-px' : 'text-slate-500 hover:text-slate-700'
            }`}
            style={tab === 'submit' ? { borderBottom: `2px solid ${color}`, color } : {}}
          >
            Submit a Complaint
          </button>
          <button
            onClick={() => setTab('status')}
            data-testid="tab-status"
            className={`flex-1 py-3 text-sm font-semibold transition ${
              tab === 'status' ? 'bg-white text-slate-900 -mb-px' : 'text-slate-500 hover:text-slate-700'
            }`}
            style={tab === 'status' ? { borderBottom: `2px solid ${color}`, color } : {}}
          >
            Check Status
          </button>
        </div>

        {tab === 'submit' && (
          <div className="p-6">
            {submitted ? (
              <div className="text-center py-4" data-testid="complaint-success">
                <div
                  className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
                  style={{ backgroundColor: `${color}22` }}
                >
                  <CheckCircle2 size={36} style={{ color }} />
                </div>
                <h2 className="text-xl font-bold text-slate-900 mb-2">Complaint Registered</h2>
                <p className="text-slate-600 text-sm mb-5">{submitted.message}</p>

                <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-4 mb-5">
                  <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Your Ticket Number</div>
                  <div className="flex items-center justify-center gap-2">
                    <span className="text-2xl font-bold font-mono" style={{ color }} data-testid="ticket-number">
                      {submitted.ticket_number}
                    </span>
                    <button
                      onClick={copyTicket}
                      className="p-1.5 text-slate-400 hover:opacity-80 transition"
                      title="Copy ticket number"
                    >
                      <Copy size={16} />
                    </button>
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800 text-left flex gap-2 mb-5">
                  <AlertCircle size={14} className="flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Important:</strong> If you call us, mention this ticket number so our agent can quickly pull up your case.
                  </span>
                </div>

                <button
                  onClick={resetForm}
                  className="w-full py-3 rounded-lg border-2 border-slate-200 text-slate-700 font-medium hover:bg-slate-50"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Your Name</label>
                  <div className="relative">
                    <UserIcon size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Full name"
                      data-testid="complaint-name-input"
                      className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': color }}
                      required
                      autoComplete="name"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Phone Number</label>
                  <div className="relative">
                    <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="10-digit mobile number"
                      data-testid="complaint-phone-input"
                      className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2"
                      style={{ '--tw-ring-color': color }}
                      required
                      autoComplete="tel"
                      inputMode="tel"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Type of Issue</label>
                  <div className="relative">
                    <Tag size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <select
                      value={problemType}
                      onChange={(e) => setProblemType(e.target.value)}
                      data-testid="complaint-type-input"
                      className="w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 bg-white appearance-none"
                      style={{ '--tw-ring-color': color }}
                    >
                      <option value="">Select a category</option>
                      {problemTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">Describe Your Issue</label>
                  <textarea
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    placeholder="Tell us what happened…"
                    rows={4}
                    data-testid="complaint-remarks-input"
                    className="w-full px-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 resize-none"
                    style={{ '--tw-ring-color': color }}
                    required
                  />
                </div>

                {submitError && (
                  <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{submitError}</div>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="complaint-submit-btn"
                  className="w-full py-3.5 rounded-lg text-white font-semibold text-base shadow-md disabled:opacity-60 flex items-center justify-center gap-2 hover:opacity-95"
                  style={{ backgroundColor: color }}
                >
                  {submitting ? <><Loader2 className="animate-spin" size={18} />Submitting…</> : (cfg.cta_text || 'Submit Complaint')}
                </button>

                {cfg.footer_text && (
                  <p className="text-xs text-slate-400 text-center">{cfg.footer_text}</p>
                )}
              </form>
            )}
          </div>
        )}

        {tab === 'status' && (
          <div className="p-6">
            {/* Mode toggle */}
            <div className="flex bg-slate-100 rounded-lg p-1 mb-4">
              <button
                type="button"
                onClick={() => { setSearchMode('ticket'); setTicketInput(''); setStatusData(null); setStatusList(null); setStatusError(''); }}
                data-testid="search-mode-ticket"
                className={`flex-1 py-2 text-xs font-semibold rounded-md transition ${
                  searchMode === 'ticket' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                }`}
              >
                By Ticket Number
              </button>
              <button
                type="button"
                onClick={() => { setSearchMode('phone'); setTicketInput(''); setStatusData(null); setStatusList(null); setStatusError(''); }}
                data-testid="search-mode-phone"
                className={`flex-1 py-2 text-xs font-semibold rounded-md transition ${
                  searchMode === 'phone' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500'
                }`}
              >
                By Phone Number
              </button>
            </div>

            <form onSubmit={handleCheck} className="space-y-4 mb-5">
              <div>
                <label className="block text-xs font-semibold text-slate-700 uppercase tracking-wider mb-1.5">
                  {searchMode === 'phone' ? 'Enter Your Phone Number' : 'Enter Your Ticket Number'}
                </label>
                <div className="relative">
                  {searchMode === 'phone'
                    ? <Phone size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    : <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />}
                  <input
                    type={searchMode === 'phone' ? 'tel' : 'text'}
                    inputMode={searchMode === 'phone' ? 'tel' : 'text'}
                    value={ticketInput}
                    onChange={(e) => setTicketInput(e.target.value)}
                    placeholder={searchMode === 'phone' ? '10-digit mobile number' : 'e.g., TKT-20260622-001'}
                    data-testid="status-ticket-input"
                    className={`w-full pl-10 pr-3 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 ${
                      searchMode === 'ticket' ? 'font-mono' : ''
                    }`}
                    style={{ '--tw-ring-color': color }}
                    required
                  />
                </div>
              </div>

              {statusError && (
                <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{statusError}</div>
              )}

              <button
                type="submit"
                disabled={checkLoading}
                data-testid="status-check-btn"
                className="w-full py-3 rounded-lg text-white font-semibold disabled:opacity-60 flex items-center justify-center gap-2 hover:opacity-95"
                style={{ backgroundColor: color }}
              >
                {checkLoading ? <><Loader2 className="animate-spin" size={18} />Checking…</> : 'Check Status'}
              </button>
            </form>

            {/* Single result (ticket search) */}
            {statusData && (
              <div className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-3" data-testid="status-result">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">Ticket</div>
                    <div className="font-mono font-bold" style={{ color }}>{statusData.ticket_number}</div>
                  </div>
                  {statusBadge(statusData.status)}
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-xs text-slate-500">Student</div>
                    <div className="text-slate-900 font-medium">{statusData.student_name}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Category</div>
                    <div className="text-slate-900 font-medium">{statusData.problem_type || '—'}</div>
                  </div>
                  <div>
                    <div className="text-xs text-slate-500">Filed On</div>
                    <div className="text-slate-700 text-xs">{new Date(statusData.created_at).toLocaleString()}</div>
                  </div>
                  {statusData.solved_at && (
                    <div>
                      <div className="text-xs text-slate-500">Solved On</div>
                      <div className="text-slate-700 text-xs">{new Date(statusData.solved_at).toLocaleString()}</div>
                    </div>
                  )}
                </div>

                {statusData.solution_remarks && (
                  <div className="bg-green-50 border border-green-200 rounded p-3">
                    <div className="text-xs text-green-700 font-semibold uppercase mb-1">Resolution</div>
                    <p className="text-sm text-green-900">{statusData.solution_remarks}</p>
                  </div>
                )}

                {statusData.status === 'open' && (
                  <p className="text-xs text-slate-500 italic">Our team will reach out within the SLA window.</p>
                )}
              </div>
            )}

            {/* List of results (phone search) */}
            {statusList && (
              <div className="space-y-3" data-testid="status-list">
                <div className="text-xs text-slate-500">
                  Found <strong>{statusList.length}</strong> {statusList.length === 1 ? 'complaint' : 'complaints'} for this number
                  {statusList.length === 10 && ' (showing latest 10)'}
                </div>
                {statusList.map((c) => (
                  <div key={c.ticket_number} className="border border-slate-200 rounded-lg p-4 bg-slate-50 space-y-2">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-mono font-bold text-sm" style={{ color }}>{c.ticket_number}</div>
                        <div className="text-xs text-slate-500">{c.problem_type || 'General'}</div>
                      </div>
                      {statusBadge(c.status)}
                    </div>
                    <div className="text-xs text-slate-500">
                      Filed on {new Date(c.created_at).toLocaleString()}
                      {c.solved_at && <> · Solved on {new Date(c.solved_at).toLocaleString()}</>}
                    </div>
                    {c.solution_remarks && (
                      <div className="bg-green-50 border border-green-200 rounded p-2 mt-1">
                        <div className="text-[10px] text-green-700 font-semibold uppercase mb-0.5">Resolution</div>
                        <p className="text-xs text-green-900">{c.solution_remarks}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="px-6 py-3 bg-slate-50 border-t border-slate-100 text-center text-xs text-slate-400">
          Exampur · Trusted by lakhs of aspirants
        </div>
      </div>
    </div>
  );
};

export default PublicComplaint;
